import type { TFunction } from 'i18next';

import type { TImporterState } from '@feathr/blackbox';

import type { TImportType } from './ImportsFilters';

interface IViewContactsTooltipParams {
  /** Whether the import produced a group to view the contacts in. */
  hasGroup: boolean;
  /** Whether the import left behind an error file, which adds a 'Download Errors' item. */
  hasErrors: boolean;
  state?: TImporterState;
}

/**
 * Whether the import is queued or mid-run. A legacy run stays `pending` throughout; the new importer
 * moves a run into `processing` (Feathr/anhinga#9328), so a check on `pending` alone misses it.
 */
export function isImportRunning(state: TImporterState | undefined): boolean {
  return state === 'pending' || state === 'processing';
}

/*
 * 'View Contacts' is disabled whenever there is no group to open, which happens for
 * four different reasons — the import hasn't run, is still running, failed, or
 * completed without matching anyone. Each gets its own reason, because telling
 * someone with a queued import that "no contacts were imported" would be wrong.
 */
function getDisabledReason(state: TImporterState | undefined, t: TFunction): string {
  if (isImportRunning(state)) {
    return t('This import is still running. Contacts will be available when it finishes.');
  }

  switch (state) {
    case 'draft':
      return t("This import hasn't run yet, so there are no contacts to view.");

    case 'failed':
      return t('This import failed, so no contacts were added.');

    /*
     * 'complete' reaches here only when the import finished without a group. Any
     * unknown state lands here too: a completed-but-empty import is the safest
     * reading of "there is no group", since the other three states are explicit.
     */
    default:
      return t('No contacts were imported.');
  }
}

/**
 * The tooltip explaining why 'View Contacts' is disabled, or `undefined` when the
 * item is enabled and needs no explanation.
 */
export function getViewContactsTooltip(
  { hasGroup, hasErrors, state }: IViewContactsTooltipParams,
  t: TFunction,
): string | undefined {
  const isViewable = state === 'complete' && hasGroup;
  if (isViewable) {
    return undefined;
  }

  const reason = getDisabledReason(state, t);
  if (!hasErrors) {
    return reason;
  }

  // 'Download Errors' sits directly below 'View Contacts' in the same menu.
  return `${reason} ${t('See Download Errors below for details.')}`;
}

/**
 * Which request the row's destructive item sends. A draft is deleted outright; anything that has
 * run is archived instead, so its contacts keep their history. The menu and the shared confirm
 * modal both read this, so the item and the request never disagree.
 */
export function getRemoveAction(state: TImporterState | undefined): 'delete' | 'archive' {
  return state === 'draft' ? 'delete' : 'archive';
}

export type TImportMenuItemId =
  | 'view-contacts'
  | 'download-file'
  | 'download-errors'
  | 'delete'
  | 'archive';

export interface IImportMenuItem {
  id: TImportMenuItemId;
  /** Why the item is disabled. Unset when the item is enabled. */
  disabledReason?: string;
}

interface IImportMenuParams {
  /** Whether the row expands into the run-details panel, which carries the error download. */
  canExpand: boolean;
  /** Whether the import produced a group to view the contacts in. */
  hasGroup: boolean;
  /** Whether the import left behind an error file. */
  hasErrors: boolean;
  kind: TImportType;
  state?: TImporterState;
}

// Placeholder copy, pending Product and Design.
function getViewContactsReason(
  { canExpand, hasErrors, state }: IImportMenuParams,
  t: TFunction,
): string {
  const reason = getDisabledReason(state, t);
  if (!hasErrors) {
    return reason;
  }
  // The error download is in the expanded row, or still in this menu for a row that can't expand.
  const pointer = canExpand
    ? t('Expand the row to download the errors.')
    : t('See Download Errors below for details.');
  return `${reason} ${pointer}`;
}

/*
 * A running import is disabled even with a group: the group may hold an earlier run's contacts
 * while this run is still writing. Otherwise a run that failed partway still has a group for the
 * contacts it did import.
 */
function getViewContactsDisabledReason(
  params: IImportMenuParams,
  t: TFunction,
): string | undefined {
  if (isImportRunning(params.state)) {
    // Placeholder copy, pending Product and Design.
    return t('This import is still running. You can view contacts when it finishes.');
  }
  return params.hasGroup ? undefined : getViewContactsReason(params, t);
}

/**
 * The row menu for an import, in order, with a reason on every disabled item.
 *
 * An item that fits the import's kind but not its current state is disabled with a reason
 * rather than left out. An item that never fits the kind is left out: a partner import adds
 * partners, not contacts, so it has no View Contacts. Download Errors is in the menu only for a
 * row that can't expand; an expandable row carries the download in its run-details panel.
 */
export function getImportMenuItems(params: IImportMenuParams, t: TFunction): IImportMenuItem[] {
  const { canExpand, hasErrors, kind, state } = params;
  const items: IImportMenuItem[] = [];

  if (kind !== 'partner') {
    items.push({
      id: 'view-contacts',
      disabledReason: getViewContactsDisabledReason(params, t),
    });
  }

  items.push({
    id: 'download-file',
    disabledReason:
      state === 'draft'
        ? t("This import hasn't run yet, so there is no file to download.")
        : undefined,
  });

  if (hasErrors && !canExpand) {
    items.push({ id: 'download-errors' });
  }

  /*
   * The server archives in any state, so this is the only guard against archiving a running
   * import, which would hide it while it keeps writing contacts.
   */
  items.push({
    id: getRemoveAction(state),
    disabledReason: isImportRunning(state)
      ? t('This import is still running. You can archive it when it finishes.')
      : undefined,
  });

  return items;
}
